/*
 * File: DatePickerInput.tsx
 * Author: Austin Robinson
 * Date Created: 08/20/2025
 * Copyright (c) DrivingDoc 2025
 */
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';

interface PropTypes extends Omit<DatePickerProps<Dayjs>, 'variant'> {
  name: string;
  label: string;
  clearable?: boolean;
}

export default function DatePickerInput({ name, label, clearable, ...props }: PropTypes) {
  const { control } = useFormContext();
  return (
    <Box>
      {label && (
        <Typography
          component={InputLabel}
          htmlFor={name}
          variant="subtitle1"
          color="text.primary"
          fontWeight={700}
          disabled={props.disabled}
        >
          {label}
        </Typography>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <DatePicker
            // slots={{ openPickerIcon: EventIcon }}
            slotProps={{
              textField: {
                id: name,
                variant: 'outlined',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                color: fieldState.error ? 'error' : 'primary',
                helperText: fieldState.error?.message,
                onBlur: field.onBlur,
                error: !!fieldState.error,
              },
              ...(clearable ? { field: { clearable: true } } : {}),
            }}
            inputRef={field.ref}
            value={field.value ? dayjs(field.value) : null}
            onChange={(value) => {
              if (value?.isValid()) {
                const newValue = dayjs(value).format('YYYY-MM-DD');
                field.onChange(newValue);
              } else if (!value) {
                field.onChange(null);
              }
            }}
            {...props}
          />
        )}
      />
    </Box>
  );
}
